import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { z } from "zod";
import { useAddBlacklistTopic, mapRpcError } from "@/hooks/useGroupBlacklist";
import { useToast } from "@/hooks/use-toast";

const topicSchema = z
  .string()
  .trim()
  .min(2, "Mínimo 2 caracteres")
  .max(80, "Máximo 80 caracteres");

export function AddTopicDialog({ groupId }: { groupId: string }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = useAddBlacklistTopic(groupId);
  const { toast } = useToast();

  const submit = async () => {
    const parsed = topicSchema.safeParse(value);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Inválido");
      return;
    }
    setError(null);
    try {
      await add.mutateAsync(parsed.data);
      toast({ title: "Tópico bloqueado", description: `"${parsed.data}" adicionado à lista.` });
      setValue("");
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Não foi possível adicionar", description: mapRpcError(err) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar tópico
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bloquear novo tópico</DialogTitle>
          <DialogDescription>
            A SYRA vai evitar puxar ou continuar conversas sobre esse assunto neste grupo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="topic">Tópico</Label>
          <Input
            id="topic"
            placeholder="ex: política partidária"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={80}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={add.isPending}>
            {add.isPending ? "Adicionando..." : "Bloquear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
